import type { CancelTokenSource } from 'axios';
import axios from 'axios';
import { useCallback, useRef, useState } from 'react';
import type { DropzoneState, FileError, FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';

import { openToastAlert } from '../../../../../common/ToastAlert/toastAlert';
import { GENERAL_ERROR_MESSAGE } from '../../../../../constants';

export type UseFileUploadResponse = {
  hovering: boolean;
  percentUploaded?: number;
  loading: boolean;
  cancelRequest: () => void;
  fileAddedName: string | null;
  onUpload: () => Promise<void>;
} & Pick<DropzoneState, 'getRootProps' | 'getInputProps'>;

export type UseFileUploadProps = {
  accept: string[];
  validator?: (file: File) => FileError | FileError[] | null;
  uploadFile: (
    file: File,
    setPercentUploaded: (percent: number) => void,
    cancelTokenSource: CancelTokenSource
  ) => Promise<void>;
  onError: (error: unknown) => void;
};

const useFileUpload = ({ accept, validator, uploadFile, onError }: UseFileUploadProps): UseFileUploadResponse => {
  const [acceptedFile, setAcceptedFile] = useState<File | null>(null);
  const [hovering, setHovering] = useState<boolean>(false);
  const [percentUploaded, setPercentUploaded] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);
  const onDragEnter = (): void => setHovering(true);
  const onDragLeave = (): void => setHovering(false);

  const cancelTokenSourceRef = useRef<CancelTokenSource>(axios.CancelToken.source());

  const onCancelUpload = useCallback((): void => {
    cancelTokenSourceRef.current.cancel();
    cancelTokenSourceRef.current = axios.CancelToken.source();
    setAcceptedFile(null);
  }, []);

  const onUpload = async (): Promise<void> => {
    if (!acceptedFile) {
      openToastAlert({
        alertType: 'error',
        message: 'Error',
        description: GENERAL_ERROR_MESSAGE,
      });

      return;
    }
    setLoading(true);

    try {
      await uploadFile(acceptedFile, setPercentUploaded, cancelTokenSourceRef.current);

      setAcceptedFile(null);
    } catch (e) {
      onError(e);
    } finally {
      setLoading(false);
    }
  };

  const onDrop = (acceptedFiles: File[], fileRejections: FileRejection[]): void => {
    if (fileRejections.length) {
      fileRejections.forEach((fileRejection) => {
        fileRejection.errors.forEach((err: { code: string; message: string }) => {
          openToastAlert({
            alertType: 'error',
            message: err.code,
            description: err.message,
          });
        });
      });
    } else if (acceptedFiles.length) {
      setAcceptedFile(acceptedFiles[0]);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept,
    multiple: false,
    disabled: false,
    onDragEnter,
    onDragLeave,
    onDrop,
    validator,
  });

  return {
    hovering,
    getRootProps,
    getInputProps,
    percentUploaded,
    loading,
    cancelRequest: onCancelUpload,
    fileAddedName: acceptedFile?.name || null,
    onUpload,
  };
};

export default useFileUpload;
